// src/app/api/journeyApi.ts
import { baseApi } from "./baseApi";
import type { QuarterKey } from "./programmeApi";
import type { MemberTier } from "./authApi";
import type { TaskStatus } from "./tasksApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type ActivityType =
  | "task_started"
  | "task_submitted"
  | "task_graded"
  | "content_viewed"
  | "content_downloaded"
  | "event_attended"
  | "fellows_nominated"
  | "fellows_selected"
  | "tier_upgraded"
  | "discussion_posted"
  | "discussion_liked"
  | "video_progress_saved";

export type EventAttendanceType =
  | "inner_circle"
  | "wider_audience"
  | "webinar"
  | "physical";

// Individual activity entry in the timeline
export interface ActivityItem {
  id: string;
  activityType: ActivityType;
  title: string;
  detail?: string;
  quarter: QuarterKey;
  score?: number | null;
  task?: string | null;
  libraryContent?: string | null;
  legacyFellow?: string | null;
  eventType?: EventAttendanceType | null;
  createdAt: string;
}

// Submission summary used inside the quarter breakdown
export interface QuarterSubmissionSummary {
  id: string;
  taskId: string;
  taskTitle: string;
  taskNumber: number;
  status: TaskStatus;
  submittedAt?: string;
  gradedAt?: string;
  score: number | null;
  feedback: string | null;
}

// Per-quarter metrics — one entry in the quarterHistory / GET /journey/quarters array
export interface QuarterData {
  quarter: QuarterKey;
  tasksTotal: number;
  tasksDone: number;
  tasksGraded: number;
  avgScore: number | null;
  contentViewed: number;
  eventAttended: boolean;
  eventType: EventAttendanceType | null;
  fellowsNominated: boolean;
  fellowsStatus: string | null;
  submissions: QuarterSubmissionSummary[];
}

// Full MenteeProfile document
export interface JourneyProfile {
  mentee: {
    firstName: string;
    lastName: string;
    email: string;
    region?: string;
    province?: string;
    ministryRole?: string;
    tier: MemberTier;
    programmeStartQuarter?: QuarterKey;
  };
  currentTier: MemberTier;
  programmeStartQuarter?: QuarterKey;
  // Overall aggregates
  totalTasksSubmitted: number;
  totalTasksGraded: number;
  totalContentViewed: number;
  totalEventsAttended: number;
  totalDiscussionPosts: number;
  quartersCompleted: number;
  overallAvgScore: number | null;
  // Fellows
  fellowsEligible: boolean;
  fellowsEligibleQuarter?: QuarterKey | null;
  // Quarter breakdown
  quarterHistory: QuarterData[];
}

// Lightweight stats for the header card
export interface JourneyStats {
  currentTier: MemberTier;
  overallAvgScore: number | null;
  quartersCompleted: number;
  totalTasksSubmitted: number;
  totalTasksGraded: number;
  totalContentViewed: number;
  totalEventsAttended: number;
  fellowsEligible: boolean;
  fellowsEligibleQuarter?: QuarterKey | null;
}

interface ProfileResponse {
  success: boolean;
  message: string;
  data: JourneyProfile;
}

interface QuartersResponse {
  success: boolean;
  message: string;
  data: QuarterData[];
}

interface ActivityResponse {
  success: boolean;
  message: string;
  count: number;
  total: number;
  page: number;
  pages: number;
  data: ActivityItem[];
}

interface StatsResponse {
  success: boolean;
  message: string;
  data: JourneyStats;
}

interface MessageResponse {
  success: boolean;
  message: string;
  data: unknown;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const journeyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/journey/profile — full MenteeProfile for My Journey hero + stats
    getJourneyProfile: builder.query<ProfileResponse, void>({
      query: () => "/journey/profile",
      providesTags: [{ type: "JourneyProfile", id: "CURRENT" }],
    }),

    // GET /api/journey/quarters — per-quarter breakdown for timeline + detail panel
    getJourneyQuarters: builder.query<QuartersResponse, void>({
      query: () => "/journey/quarters",
      providesTags: [{ type: "JourneyQuarters", id: "ALL" }],
    }),

    // GET /api/journey/activity — paginated activity log for the timeline
    getActivity: builder.query<
      ActivityResponse,
      {
        quarter?: QuarterKey;
        type?: ActivityType;
        limit?: number;
        page?: number;
      }
    >({
      query: (params) => ({ url: "/journey/activity", params }),
      providesTags: [{ type: "JourneyActivity", id: "LIST" }],
    }),

    // GET /api/journey/stats — lightweight header card stats
    getJourneyStats: builder.query<StatsResponse, void>({
      query: () => "/journey/stats",
      providesTags: [{ type: "JourneyProfile", id: "STATS" }],
    }),

    // POST /api/journey/attendance — Portal Manager records event attendance
    recordAttendance: builder.mutation<
      MessageResponse,
      {
        menteeId: string;
        quarter: QuarterKey;
        cycle?: string;
        attendanceType: EventAttendanceType;
        attendedAt?: string;
        notes?: string;
      }
    >({
      query: (body) => ({ url: "/journey/attendance", method: "POST", body }),
      invalidatesTags: [
        { type: "JourneyProfile", id: "CURRENT" },
        { type: "JourneyQuarters", id: "ALL" },
        { type: "JourneyActivity", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetJourneyProfileQuery,
  useGetJourneyQuartersQuery,
  useGetActivityQuery,
  useGetJourneyStatsQuery,
  useRecordAttendanceMutation,
} = journeyApi;
